/**
 * Plan 10, Task 4.2 — Debug overlay: fixed-position FPS / draw-call HUD.
 *
 * Mounts a `<div class="smashparty-debug-overlay">` over the canvas with
 * real-time performance stats. Hidden by default; opt in with
 * VITE_RENDER_DEBUG_OVERLAY=1 when profiling locally.
 */

import type { DebugOverlayHandle, PerfStats } from './types';

export interface DebugOverlayConfig {
  /** Parent element. Default: document.body. */
  readonly parent?: HTMLElement;
  /**
   * Initial visibility. Default: VITE_RENDER_DEBUG_OVERLAY=1.
   * Tests inject `visible: true` to bypass env detection.
   */
  readonly visible?: boolean;
}

export function createDebugOverlay(config?: DebugOverlayConfig): DebugOverlayHandle {
  const parent = config?.parent ?? document.body;

  // Determine initial visibility: explicit config takes priority; otherwise
  // the renderer HUD is opt-in for local profiling.
  const initialVisible =
    config?.visible !== undefined
      ? config.visible
      : import.meta.env?.VITE_RENDER_DEBUG_OVERLAY === '1';

  // ── Create element ──────────────────────────────────────────────────────────
  const element = document.createElement('div');
  element.className = 'smashparty-debug-overlay';
  element.style.cssText = [
    'position: fixed',
    'top: 8px',
    'right: 8px',
    'z-index: 9999',
    'background: rgba(0,0,0,0.6)',
    'color: #fff',
    'font: 12px monospace',
    'padding: 6px 8px',
    'border-radius: 4px',
    'pointer-events: none',
  ].join('; ');

  if (!initialVisible) {
    element.style.display = 'none';
  }

  parent.appendChild(element);

  // ── Handle ──────────────────────────────────────────────────────────────────
  let disposed = false;

  function update(stats: PerfStats): void {
    element.textContent = `FPS ${stats.fps.toFixed(0)} · DC ${stats.drawCalls} · Tris ${stats.triangles}`;
  }

  function setVisible(visible: boolean): void {
    element.style.display = visible ? '' : 'none';
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    if (element.parentNode) {
      element.parentNode.removeChild(element);
    }
  }

  return { element, update, setVisible, dispose };
}
